import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { idr } from '@shared/format';

export interface PaidTopup {
  amount: number;
  bonus_amount: number;
  credited_amount: number;
  method: string;
}

const METHOD_LABEL: Record<string, string> = {
  QRIS: 'QRIS',
  EWALLET_GOPAY: 'GoPay',
  EWALLET_OVO: 'OVO',
  VA_BCA: 'Virtual Account BCA',
  VA_BNI: 'Virtual Account BNI',
  VA_MANDIRI: 'Virtual Account Mandiri',
  CARD: 'Kartu kredit/debit',
  MANUAL: 'Manual',
};

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Eased count-up. */
function useCountUp(target: number, delayMs: number, durationMs = 900): number {
  const [value, setValue] = useState(() => (reducedMotion() ? target : 0));
  useEffect(() => {
    if (reducedMotion()) return;
    let frame = 0;
    const start = performance.now() + delayMs;
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - start) / durationMs));
      // ease-out cubic
      setValue(Math.round(target * (1 - (1 - t) ** 3)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, delayMs, durationMs]);
  return value;
}

export function TopupSuccess({ payment, balance, onClose }: { payment: PaidTopup; balance: number | null; onClose: () => void }) {
  const credited = useCountUp(payment.credited_amount, 450);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = before;
      window.removeEventListener('keydown', onKey);
      opener?.focus({ preventScroll: true });
    };
  }, [onClose]);

  return createPortal(
    <div className="ts-backdrop" onMouseDown={onClose}>
      <div
        className="ts-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ts-title"
        aria-describedby="ts-amount"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="ts-check" aria-hidden>
          <svg viewBox="0 0 52 52">
            <circle className="ts-check-circle" cx="26" cy="26" r="24" />
            <path className="ts-check-mark" d="M15 27l7 7 15-15" />
          </svg>
        </div>

        <h2 id="ts-title" className="ts-title">
          Saldo berhasil ditambahkan
        </h2>
        <div id="ts-amount" className="ts-amount" aria-label={`Bertambah ${idr(payment.credited_amount)}`}>
          +{idr(credited)}
        </div>

        <dl className="ts-rows">
          <div>
            <dt>Dibayar</dt>
            <dd>{idr(payment.amount)}</dd>
          </div>
          {payment.bonus_amount > 0 && (
            <div>
              <dt>Bonus saldo</dt>
              <dd className="ts-bonus">+{idr(payment.bonus_amount)}</dd>
            </div>
          )}
          <div>
            <dt>Metode</dt>
            <dd>{METHOD_LABEL[payment.method] ?? payment.method}</dd>
          </div>
          {balance !== null && (
            <div className="ts-total">
              <dt>Saldo sekarang</dt>
              <dd>{idr(balance)}</dd>
            </div>
          )}
        </dl>

        <button ref={closeRef} className="btn btn-primary btn-block btn-lg" onClick={onClose}>
          Selesai
        </button>
        <Link className="ts-link" to="/transactions" onClick={onClose}>
          Lihat transaksi
        </Link>
      </div>
    </div>,
    document.body,
  );
}
